import React from "react";
import { Link } from "react-router-dom";
import { Heart, Gift, Shield, Users, Sparkles, ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import Logo from "../components/Logo";

const HERO_IMG = "https://images.pexels.com/photos/6957900/pexels-photo-6957900.jpeg";
const COMMUNITY_IMG = "https://images.pexels.com/photos/8770951/pexels-photo-8770951.jpeg";
const COMFORT_IMG = "https://images.pexels.com/photos/6690207/pexels-photo-6690207.jpeg";

export default function Landing() {
  const { user } = useAuth();
  const primaryCTA = user ? { to: "/dashboard", label: "Go to my registry" } : { to: "/register", label: "Start your registry" };

  return (
    <div data-testid="landing-page">
      {/* Hero */}
      <section className="relative overflow-hidden hero-bloom">
        <div className="container-x pt-16 pb-20 lg:pt-24 lg:pb-32">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 animate-fade-up">
              <Logo size={56} showWordmark={false} className="mb-6" />
              <span className="tag-pill" data-testid="landing-tagline">For women, by women</span>
              <h1 className="mt-5 font-outfit text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-ink leading-[1.05]">
                Recovery is<br />
                a group project.
              </h1>
              <p className="mt-6 font-manrope text-lg text-ink-soft max-w-xl leading-relaxed">
                Hysteregistry is a warm, shareable list of what you actually need to recover from a
                hysterectomy — so the people who love you can help, without guessing.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to={primaryCTA.to} className="btn-primary inline-flex items-center gap-2" data-testid="hero-cta-primary">
                  {primaryCTA.label} <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/browse" className="btn-ghost" data-testid="hero-cta-secondary">
                  Browse curated items
                </Link>
              </div>
              <div className="mt-10 flex items-center gap-6 font-manrope text-sm text-ink-soft">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-brand" /> Address always private
                </div>
                <div className="flex items-center gap-2">
                  <Heart className="h-4 w-4 text-accent2" /> Duplicate-proof claims
                </div>
              </div>
            </div>

            <div className="lg:col-span-5 relative animate-fade-up" style={{ animationDelay: "120ms" }}>
              <div className="relative rounded-[2rem] overflow-hidden card-soft">
                <img
                  src={HERO_IMG}
                  alt="A woman resting peacefully in a bright, cozy room"
                  className="w-full h-[460px] object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 card-soft p-5 max-w-[240px] hidden sm:block">
                <p className="font-outfit text-sm text-ink font-medium">Week of Oct 14</p>
                <p className="font-manrope text-xs text-ink-soft mt-1">
                  When Sarah's gifts need to arrive — no awkward questions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="container-x py-20" data-testid="how-it-works">
        <div className="max-w-2xl">
          <p className="tag-pill">How it works</p>
          <h2 className="mt-4 font-outfit text-3xl lg:text-4xl font-medium text-ink tracking-tight">
            Three small steps. A whole lot of relief.
          </h2>
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {[
            {
              icon: Gift,
              step: "01",
              title: "Build your list",
              body: "Add items from our curated recovery collection or paste any product URL. One-click add. Done.",
            },
            {
              icon: Users,
              step: "02",
              title: "Share with loved ones",
              body: "Send a private link. They sign in, claim items, and Hysteregistry prevents duplicate gifts.",
            },
            {
              icon: Sparkles,
              step: "03",
              title: "Gifts arrive, you rest",
              body: "Friends ship directly to your saved address — we pass it through at checkout, never display it.",
            },
          ].map(({ icon: Icon, step, title, body }, idx) => (
            <div
              key={step}
              className="card-soft card-soft-hover p-8 animate-fade-up"
              style={{ animationDelay: `${idx * 90}ms` }}
              data-testid={`how-step-${step}`}
            >
              <div className="flex items-center justify-between">
                <div className="h-12 w-12 rounded-full bg-brand-soft flex items-center justify-center">
                  <Icon className="h-5 w-5 text-brand" strokeWidth={1.6} />
                </div>
                <span className="font-outfit text-sm text-ink-soft">{step}</span>
              </div>
              <h3 className="mt-6 font-outfit text-xl font-medium text-ink">{title}</h3>
              <p className="mt-3 font-manrope text-sm text-ink-soft leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Community band */}
      <section className="container-x py-20">
        <div className="card-soft overflow-hidden relative">
          <div className="grid lg:grid-cols-2 items-stretch">
            <div className="p-10 lg:p-14">
              <p className="tag-pill">Why Hysteregistry</p>
              <h2 className="mt-4 font-outfit text-3xl lg:text-4xl font-medium text-ink tracking-tight">
                Because no one should Google “what do I need after a hysterectomy” at 2 a.m.
              </h2>
              <p className="mt-5 font-manrope text-ink-soft leading-relaxed">
                Around 600,000 hysterectomies happen in the US every year. We've made them feel
                clinical for too long. Hysteregistry is soft, clear, and built around what actually
                helps — rest, comfort, food you don't have to cook, and people who want to show up.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to={primaryCTA.to} className="btn-primary" data-testid="community-cta">
                  {primaryCTA.label}
                </Link>
                <Link to="/browse" className="btn-ghost" data-testid="community-browse">
                  See what people are adding
                </Link>
              </div>
            </div>
            <div className="relative min-h-[320px]">
              <img
                src={COMMUNITY_IMG}
                alt="Women supporting each other"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Category preview */}
      <section className="container-x py-20" data-testid="category-preview">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div className="max-w-xl">
            <p className="tag-pill">Curated for you</p>
            <h2 className="mt-4 font-outfit text-3xl lg:text-4xl font-medium text-ink tracking-tight">
              Five categories. All the things that actually help.
            </h2>
          </div>
          <Link to="/browse" className="btn-ghost" data-testid="preview-browse-all">
            Browse all categories
          </Link>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
          {[
            { name: "Recovery", blurb: "Pillows, binders, heat." },
            { name: "Comfort", blurb: "Blankets, robes, socks." },
            { name: "Self Care", blurb: "Dry shampoo. Silk." },
            { name: "Entertainment", blurb: "Books, audiobooks, puzzles." },
            { name: "Nutrition", blurb: "Meals, broth, snacks." },
          ].map((c, i) => (
            <Link
              to={`/browse?category=${encodeURIComponent(c.name)}`}
              key={c.name}
              className="card-soft card-soft-hover p-6 animate-fade-up"
              style={{ animationDelay: `${i * 60}ms` }}
              data-testid={`preview-cat-${c.name}`}
            >
              <div className="h-20 w-20 rounded-2xl bg-brand-soft mb-4 flex items-center justify-center">
                <Heart className="h-7 w-7 text-brand" strokeWidth={1.5} />
              </div>
              <h3 className="font-outfit text-lg font-medium text-ink">{c.name}</h3>
              <p className="font-manrope text-sm text-ink-soft mt-1">{c.blurb}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* Closing CTA */}
      <section className="container-x py-20">
        <div className="card-soft p-10 lg:p-16 relative overflow-hidden text-center">
          <div
            className="absolute inset-0 -z-0 grain opacity-40"
            aria-hidden
          />
          <div className="relative z-10 max-w-2xl mx-auto">
            <h2 className="font-outfit text-3xl lg:text-5xl font-medium tracking-tight text-ink">
              Your recovery, <span className="text-brand">supported</span>.
            </h2>
            <p className="mt-5 font-manrope text-ink-soft leading-relaxed">
              It takes two minutes. Build your list, set your surgery week, share the link. Let the
              people who love you actually help.
            </p>
            <div className="mt-8 flex justify-center gap-3 flex-wrap">
              <Link to={primaryCTA.to} className="btn-primary" data-testid="closing-cta-primary">
                {primaryCTA.label}
              </Link>
              <Link to="/login" className="btn-ghost" data-testid="closing-cta-login">
                I already have an account
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
